import type { AccountType, IDatesRange, TExportTargets, TImportTargets } from "types";

import type { AgreementStatus } from "./agreementsStore";
import type { ArticleCompletionStatus } from "./articleStore";
import type { ICustomField } from "./customFieldsStore";
import type { ImportSourceType } from "./filesHistorySlice";

export type TFetchStatus = "idle" | "pending" | "succeeded" | "failed" | "fulfilled" | "rejected";

export enum EFetchStatus {
    IDLE = "idle",
    PENDING = "pending",
    FULFILLED = "fulfilled",
    REJECTED = "rejected",
}

export type TStock = {
    id: number;
    isbn: string;
    name: string;
    grossQuantities: number;
    returnsQuantities: number;
    netQuantities: number;
    initialInventory: number;
    printedInventory: number;
    locations: TLocation[];
    totalLocationsInventory: number;
    totalScrappedInventory: number;
    totalPressInventory: number;
    theoricSales: number;
    control: number;
    avatarUrl?: string;
};
export type TLocation = {
    titleId: number;
    locationId: number;
    id: number;
    inventory: number;
    scrappedInventory: number;
    pressInventory: number;
    name: any;
};
export type TChangeStock = {
    printedInventory: number;
    locations: Partial<TLocation>[];
};

// Agreement signed status for paginated request
export type AgreementSignedStatus = "all" | "tosend" | "sent" | "signed";

export type TFilterParamsPaymentStatus = "all" | "paid" | "topay" | "unpaid";

export type TParamsPagination = {
    periodId?: number | number[];
    offset?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: string | "DESC" | "ASC" | "asc" | "desc";
    query?: string;
    tags?: string[];
    titleTags?: string[];
    beneficiaryTags?: string[];
    brand?: string;
    /**
     * When set to "0", only returns titles without any term.
     */
    hasTerm?: "0" | "1";
    /**
     * Beneficiaries with a recorded succession are hidden unless set to "1".
     */
    includeDeceased?: "0" | "1";
    companyIds?: number[];
    extraParams?: any;
    control?: "all" | "invalid" | "valid";
    paidStatus?: TFilterParamsPaymentStatus;
    grossDuty?: string;
    /**
     * Used for the termTransferSales.
     */
    status?: "all" | number;
    year?: number;
    /**
     * For now, used in termTransferSales ("advance" or "sales")
     */
    type?: string;
    signedStatus?: AgreementSignedStatus;
    dates?: IDatesRange;
    completionStatus?: ArticleCompletionStatus | "all";
    agreementStatus?: AgreementStatus | "all";
    export?: "xlsx";
    paymentLot?: number;
    beneficiaryIds?: number[];
    /**
     * Used to filter for advance
     */
    isForfait?: "0" | "1" | "all";
    /**
     * Advance custom status. "none" keeps slices without one.
     */
    customStatusId?: string;
    /**
     * ID of the termPeriodData that has been selected.
     */
    connectedTPD?: number;
    method?: "all" | "GET" | "POST" | "PUT" | "DELETE";
    target?: Array<TImportTargets | TExportTargets> | "all";
    termId?: number | undefined;
    sourceType?: ImportSourceType | "all";
};

export interface ILastStatement {
    termId?: number;
    titleId: number | undefined;
    name: string;
    grossDuty: number;
    netAmount: number;
    periodId: number;
    periodStart: Date;
    periodEnd: Date;
    paymentDate?: Date;
    company: string;
    companyId: number | undefined;
    companyAvatarUrl: string;
    avatarUrl: string;
    isPeriodActive: number;
    isActive: number;
    representative: string;
    id: number; // beneficiarId or advancePaymentId
    sliceId?: number;
    type: "title" | "consolidated" | "advance" | "article";
    contractSigned?: number;
    precount?: string;
    hasCollectiveManagement?: boolean;
    contributions?: number;
}

export interface AccountingEntry {
    id?: number;
    companyId?: number;
    // status: 'saved' | 'draft' | 'edited';
    filters: AccountingFilter;
    mapping?: AccountingMapping[];
    name: string;
    note: string;
    matchingBeneficiaries: number;
    analysisAxis: string;
}

export interface AccountingMapping {
    id?: number;
    entryId?: number;
    accountType: AccountingType;
    accountNumber: string | number;
    direction: "credit" | "debit";
    appearanceOrder: number;
    key: number | string;
    exportType: "payment" | "defer" | "advanceReduction" | "allowance";
    accountCategory: AccountingAccountCategory;
    attachAuxiliaryOnExport: number;
    journalCode: string;
    gect?: AccountingMappingGect | null;
}

export const GECT_VAT_AMOUNT_TYPES = ["vat10", "vat92"] as const;

export type GectVatAmountType = (typeof GECT_VAT_AMOUNT_TYPES)[number];

/**
 * Cegid GECT configuration of a mapping line (accounting software `cegid_gect`)
 */
export interface AccountingMappingGect {
    documentType?: string;
    label?: string;
    poste?: string;
    budgetControl?: string;
    analyticThirdParty?: string;
    vatCode?: string;
    /** Crealo VAT exported as the VAT amount of a line with a VAT code */
    vatAmount?: GectVatAmountType;
    /** The line is exported only for beneficiaries with one of these VAT profiles, every profile when empty */
    vatProfiles?: BeneficiaryVAT[];
    articleNature?: string;
    dueNumber?: number;
    vatDeclarationOnly?: boolean;
    omitContractParams?: boolean;
    contractNumbers?: boolean;
}

export type AccountingType =
    | "distributorContributions"
    | "vat10"
    | "vat92"
    | "vatIntracom"
    | "totalContributions"
    | "socialContributions"
    | "netToPay"
    | "foreignWithholding"
    | "previousPositiveBalance"
    | "grossDuty"
    | "netDuty"
    | "distributorContribution"
    | "distributorTraining"
    | "oldAgeUncapped"
    | "oldAgeCapped"
    | "csgDeductible"
    | "csgNonDeductible"
    | "crds"
    | "professionalTraining";

export const AccountingExportMapKeys = {
    default: [
        "distributorContributions",
        "vat10",
        "vat92",
        "vatIntracom",
        "totalContributions",
        "socialContributions",
        "netToPay",
        "foreignWithholding",
        "previousPositiveBalance",
        "grossDuty",
        // Social contributions one by one
        "distributorContribution",
        "distributorTraining",
        "oldAgeUncapped",
        "oldAgeCapped",
        "csgDeductible",
        "csgNonDeductible",
        "crds",
        "professionalTraining",
    ],
    advanceReduction: ["grossDuty"],
    allowance: ["grossDuty", "netDuty", "vat10"],
};

export const AccountingAccountCategories = ["G", "A", "X", "E"];

export enum AccountingAccountCategory {
    /**
     * Compte général | same as blank value
     */
    GENERAL = "G",
    /**
     * Compte analytique
     */
    ANALYTICAL = "A",
    /**
     * Compte auxiliaire
     */
    AUXILIARY = "X",
    /**
     * Echéance
     */
    ECHEANCE = "E",
}

export interface AccountingFilter {
    typology?: string | "all";
    precount?: 0 | 1 | "all";
    vat?: 0 | 1 | 2 | "all";
    relatedResource?: "advance" | "advanceForfait" | "term" | "all";
    // paidStatus?: 0 | 1 | 2; // We keep this for a future accounting update
}

export interface IBeneficiariesCompanies {
    id: number;
    name: string;
    avatarUrl: string;
    publisherType: "book" | "press" | "audiovisual";
    settings: { id: number; name: string; content: any }[];
    currency: string;
    customFields?: ICustomField[];
    countryCode: string;
    countryFeatures: import("types/countryFeatures").CountryFeatures;
}

export interface IAnalyticalCodes {
    id: number;
    name: string;
    companyId: string;
    created: string;
}

export enum ChannelType {
    QUANTITY_BASED = 1,
    AMOUNT_BASED = 2,
    FOLIO_BASED = 3,
}

export enum ChannelKind {
    PHYSICAL = 1,
    DIGITAL = 2,
    RIGHTS_TRANSFER = 3,
    COLLECTIVE_MANAGEMENT = 4,
    OTHER = 5,
}

export enum ChannelCalculationMethod {
    PROPORTIONAL = 1,
    PACKAGE = 2,
    SUBSCRIPTION = 3,
}

export enum PeriodActiveStatus {
    PENDING = 2,
    OPENED = 1,
    CLOSED = 0,
}

export type TChannelsValues = {
    [index: number]: Array<{
        id: number;
        start: number;
        quantity: number | string;
        percentage: number | string;
        kind: number | undefined;
    }>;
};

export type TAccountType = AccountType;

export enum PeriodStatus {
    CLOSED = 0,
    CURRENTLY_OPEN = 1,
    FUTURE = 2,
}

export enum PaymentStatus {
    NOT_PAID = 0,
    TO_PAY = 1,
    PAID = 2,
}

export enum BeneficiaryVAT {
    RETENUE_SOURCE = 0,
    // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
    WITHHELD_VAT = 0,
    VAT = 1,
    EXEMPT = 2,
    // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
    FRANCHISE = 2,
    // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
    NONE = 2,
    TWENTY_PERCENT = 3,
    // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
    TPS_ONLY = 4,
    TPS_TVQ = 5,
}

export enum TBeneficiaryPrecount {
    NOT_PRECOUNTED = 0,
    PRECOUNTED = 1,
    CONDITIONAL = 2,
}

export enum EPortalType {
    BOOK = "book",
    PRESS = "press",
    URSSAF = "urssaf",
    BENEFICIARY = "beneficiary",
    AUDIOVISUAL = "audiovisual",
}

export type TPortalType = "book" | "press" | "urssaf" | "beneficiary" | "audiovisual";
